/**
 * Sagas for the /admin/app-config definition sync.
 *
 * `initAdminPage` forks the watchers and returns. It deliberately does NOT run
 * the first scan itself: `runRouteSaga` catches a route saga's throw and does
 * not restart it (see common-app/state/modules/routing/sagas.ts), so anything
 * that can fail inside the init path can take every watcher forked beside it
 * down with it — leaving a page whose buttons render and do nothing. The page
 * dispatches the scan and the root-org check on mount instead, the same shape
 * the Watermarks page uses.
 *
 * After any successful mutation the scan is re-run rather than the store being
 * patched optimistically, so what the admin sees is the environment rather
 * than what we hoped it became. That covers the set-a-default write too, which
 * shares UPDATE_DEFINITION_SUCCESS — it is the same outcome in the same shape,
 * and the row reports it the same way.
 *
 * Every request watcher drops its action for a session denied the app-config
 * tool (VE-30689) — a second layer behind the page, which never renders for
 * one. That is the SUPERUSER bar, so an Organization Admin is denied even in
 * the root org — Redact policy rather than a platform rule, explained in the
 * header of `../selectors`.
 *
 * That re-scan is HELD BRIEFLY, which is the one piece of timing in this file
 * that is about the operator rather than the API — see SUCCESS_HOLD_MS.
 */
import {
  delay,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import * as Actions from '../actions';
import { selectIsAppConfigAccessDenied } from '../selectors';
import * as Services from '../services';

/**
 * The scan and every mutation pass through here first (VE-30689).
 *
 * The page already never renders for a denied session, so nothing should
 * dispatch these then. This is the second layer: the scan returns every
 * organization's definitions, and one stray dispatch should not be all that
 * stands between a non-superuser and that read.
 */
function* isDenied() {
  return yield* select(selectIsAppConfigAccessDenied);
}

export function* watchScanDefinitions() {
  // takeLatest here is the house pattern, not a guarantee: this generator
  // finishes the moment it dispatches the thunk, so there is no task left to
  // cancel and the paged scan runs on the thunk middleware outside saga
  // supervision. Two overlapping scans can still land out of order. Closing
  // that would need a request id carried through SCAN_DEFINITIONS_SUCCESS.
  yield* takeLatest(Actions.SCAN_DEFINITIONS_REQUEST, function* () {
    if (yield* isDenied()) {
      return;
    }
    yield* put(Services.scanDefinitions());
  });
}

export function* watchCheckRootOrganization() {
  yield* takeLatest(Actions.CHECK_ROOT_ORGANIZATION_REQUEST, function* () {
    yield* put(Services.checkRootOrganization());
  });
}

export function* watchAddDefinitions() {
  // takeEvery: two rows added in quick succession are two different intents.
  yield* takeEvery(Actions.ADD_DEFINITIONS_REQUEST, function* (action) {
    if (yield* isDenied()) {
      return;
    }
    yield* put(Services.createDefinitions(action.payload.definitions));
  });
}

export function* watchUpdateDefinition() {
  yield* takeEvery(Actions.UPDATE_DEFINITION_REQUEST, function* (action) {
    if (yield* isDenied()) {
      return;
    }
    yield* put(Services.updateDefinition(action.payload.definition));
  });
}

export function* watchSetDefault() {
  yield* takeEvery(Actions.SET_DEFAULT_REQUEST, function* (action) {
    if (yield* isDenied()) {
      return;
    }
    yield* put(Services.setDefinitionDefault(action.payload));
  });
}

export function* watchDeleteDefinition() {
  yield* takeEvery(Actions.DELETE_DEFINITION_REQUEST, function* (action) {
    if (yield* isDenied()) {
      return;
    }
    yield* put(Services.deleteDefinition(action.payload.configKey));
  });
}

/**
 * How long the re-scan waits after a success.
 *
 * Registering a key moves its row out of Missing and into In sync, so the only
 * thing an immediate re-scan shows is the row disappearing from where you were
 * looking. The pause is long enough to read a tick where the button was, and
 * short enough that nobody waits on it.
 */
export const SUCCESS_HOLD_MS = 900;

/**
 * How long the row keeps saying it worked.
 *
 * Outlives the re-scan on purpose: the row moves to another bucket partway
 * through, and the marker going with it is what lets an operator follow the
 * key to its new home instead of hunting for it.
 */
export const SUCCESS_VISIBLE_MS = 4500;

/**
 * Re-reads the environment after anything that changed it.
 *
 * takeLatest, not takeEvery: three rows actioned in quick succession would
 * otherwise start three full paged scans at once, and the one that happens to
 * RESOLVE last wins regardless of which started last. One refetch after the
 * final change is both cheaper and correct. Matches watermarks/sagas.
 *
 * takeLatest also makes the hold below safe: a second success during the pause
 * cancels this task and restarts the wait, so a burst of adds still ends in
 * exactly one scan.
 */
export function* watchMutationSuccess() {
  yield* takeLatest(
    [
      Actions.ADD_DEFINITIONS_SUCCESS,
      Actions.UPDATE_DEFINITION_SUCCESS,
      Actions.DELETE_DEFINITION_SUCCESS,
    ],
    function* () {
      yield* delay(SUCCESS_HOLD_MS);
      yield* put(Actions.actionScanDefinitions());
    }
  );
}

/**
 * Expires each success marker on its own timer.
 *
 * takeEvery, not takeLatest: two rows added a second apart are two markers
 * with two deadlines, and takeLatest would cancel the first row's timer when
 * the second started — leaving a tick on screen forever.
 */
export function* watchClearDefinitionSuccess() {
  yield* takeEvery(
    [
      Actions.ADD_DEFINITIONS_SUCCESS,
      Actions.UPDATE_DEFINITION_SUCCESS,
      Actions.DELETE_DEFINITION_SUCCESS,
    ],
    function* (action) {
      const payload = action.payload as {
        configKeys?: readonly string[];
        configKey?: string;
      };
      const configKeys =
        payload.configKeys ?? (payload.configKey ? [payload.configKey] : []);
      yield* delay(SUCCESS_VISIBLE_MS);
      yield* put(Actions.CLEAR_DEFINITION_SUCCESS({ configKeys }));
    }
  );
}

export function* initAdminPage() {
  yield* fork(watchScanDefinitions);
  // NOT watchCheckRootOrganization — that one is forked app-wide from the root
  // saga, because the side nav asks the same question from every page. Forking
  // it here as well would run the query twice on this route.
  yield* fork(watchAddDefinitions);
  yield* fork(watchUpdateDefinition);
  yield* fork(watchSetDefault);
  yield* fork(watchDeleteDefinition);
  yield* fork(watchMutationSuccess);
  yield* fork(watchClearDefinitionSuccess);
}
